import type { ApiRequest, ApiResponse } from './transport.ts'

/** RFC 9457 problem detail as this API emits it. `code` is the only thing to switch on. */
export interface Problem {
  readonly type?: string
  readonly title: string
  readonly status: number
  readonly detail: string
  readonly code: string
  readonly timestamp: string
  readonly traceId?: string
  readonly instance?: string
  readonly [property: string]: unknown
}

export class ApiProblemError extends Error {
  readonly code: string
  readonly status: number
  readonly problem: Problem
  readonly request: ApiRequest
  constructor(problem: Problem, request: ApiRequest) {
    super(`${problem.code}: ${problem.detail}`)
    this.name = 'ApiProblemError'
    this.problem = problem
    this.request = request
    this.code = problem.code
    this.status = problem.status
  }

  property<T = unknown>(name: string): T | undefined {
    return this.problem[name] as T | undefined
  }
}

export type UnauthenticatedReason =
  /** The token was rejected and renewing it produced nothing. */
  | 'sign-in-required'
  /** Renewal itself failed - the refresh token is spent, revoked, or the provider is down. */
  | 'renewal-failed'
  /** JSON was expected and HTML arrived: something in front of the API is answering for it. */
  | 'not-the-api'

/** The user must sign in again. Never retried, and never a toast: it is a state, not an error. */
export class ApiUnauthenticatedError extends Error {
  readonly code = 'client.unauthenticated'
  readonly reason: UnauthenticatedReason
  readonly request: ApiRequest
  constructor(reason: UnauthenticatedReason, request: ApiRequest) {
    super(`Not authenticated (${reason})`)
    this.name = 'ApiUnauthenticatedError'
    this.reason = reason
    this.request = request
  }
}

export class ApiMalformedResponseError extends Error {
  readonly code = 'client.malformed_response'
  readonly request: ApiRequest
  readonly response: ApiResponse
  constructor(request: ApiRequest, response: ApiResponse) {
    super(`${request.method} ${request.url} returned an unreadable body`)
    this.name = 'ApiMalformedResponseError'
    this.request = request
    this.response = response
  }
}

/**
 * Codes that mean "this token is no longer good, get another one". Every other 401 is
 * terminal for this principal - renewing would succeed and the next call would fail again.
 */
export const RENEWABLE_CODES: ReadonlySet<string> = new Set([
  'auth.unauthorized',
  'auth.token_expired',
  'auth.token_invalid',
])

/** Terminal: the principal authenticated, but this installation has no account for them. */
export const NOT_PROVISIONED_CODE = 'auth.not_provisioned'

/** Terminal: consent was withdrawn or the account was disabled. Renewal cannot fix it. */
export const REVOKED_CODE = 'auth.token_revoked'

/**
 * `code` is this API's own extension, not an RFC 9457 field: a framework-level failure (a
 * request body Jackson can't even deserialize, say) answers with a bare `{title, status,
 * detail}` and no `code` at all. Requiring `code` here used to turn that body into an
 * unreadable `ApiMalformedResponseError`, discarding a `detail` that was there all along -
 * `code` now defaults to `''`, which no real code ever equals, so callers that switch on it
 * fall through safely while `detail`/`title` still reach the UI.
 */
export function parseProblem(body: string): Problem | undefined {
  let parsed: unknown
  try {
    parsed = JSON.parse(body)
  } catch {
    return undefined
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined
  const candidate = parsed as Record<string, unknown>
  if (typeof candidate['status'] !== 'number') return undefined
  return {
    title: typeof candidate['title'] === 'string' ? candidate['title'] : '',
    detail: typeof candidate['detail'] === 'string' ? candidate['detail'] : '',
    code: typeof candidate['code'] === 'string' ? candidate['code'] : '',
    timestamp: typeof candidate['timestamp'] === 'string' ? candidate['timestamp'] : '',
    ...candidate,
  } as Problem
}
